import React, { useCallback, useMemo, useState } from 'react';
import { Sheet, TextArea } from 'tamagui';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';

import { CobuStack, CobuText, CobuAlert, CobuHeader } from '@cobu/ui';
import { useReportPlaceMutation } from '@cobu/api-client';
import { createReportPlaceSchema, ReportPlaceFormValues } from '../../schemas/reportPlaceSchema';
import { logger } from '../../utils/logger';
import { Strings } from '../../constants/Strings';

interface ReportPlaceModalProps {
  visible: boolean;
  onClose: () => void;
  placeId: string;
  placeName: string;
  reportedByUuid: string;
}

/**
 * Report-a-place sheet — mirrors ReportUserModal's structure (Cancel/title/
 * Submit header, reason textarea, success/error alerts) backed by the
 * Places mock report mutation instead of the real user-report API.
 */
export function ReportPlaceModal({ visible, onClose, placeId, placeName, reportedByUuid }: ReportPlaceModalProps) {
  const strings = Strings.get().places.report;
  const schema = useMemo(() => createReportPlaceSchema(), []);
  const { control, handleSubmit, reset, formState } = useForm<ReportPlaceFormValues>({
    resolver: zodResolver(schema),
    defaultValues: { reason: '' },
    mode: 'onChange',
  });
  const reportPlaceMutation = useReportPlaceMutation();

  const [successAlertOpen, setSuccessAlertOpen] = useState(false);
  const [errorAlertOpen, setErrorAlertOpen] = useState(false);

  const isSubmitDisabled = !formState.isValid || reportPlaceMutation.isPending;

  const onSubmit = async ({ reason }: ReportPlaceFormValues) => {
    const trimmedReason = reason.trim();
    if (!trimmedReason) return;

    try {
      await reportPlaceMutation.mutateAsync({
        place: placeId,
        reportedByUuid,
        reason: trimmedReason,
      });

      reset();
      onClose();
      setTimeout(() => setSuccessAlertOpen(true), 200);
    } catch (error) {
      logger.error('Report place submission error:', error);
      setErrorAlertOpen(true);
    }
  };

  const handleSubmitPress = useCallback(() => {
    if (isSubmitDisabled) return;
    handleSubmit(onSubmit)();
  }, [handleSubmit, onSubmit, isSubmitDisabled]);

  const handleClose = () => {
    reset();
    onClose();
  };

  return (
    <>
      <Sheet
        open={visible}
        onOpenChange={(open: boolean) => !open && handleClose()}
        snapPoints={[80]}
        snapPointsMode="percent"
        dismissOnSnapToBottom
        dismissOnOverlayPress
        modal
      >
        <Sheet.Overlay
          animation={undefined as any}
          enterStyle={{ o: 0 }}
          exitStyle={{ o: 0 }}
          bg="$blackTransparent50"
        />
        <Sheet.Frame br="$3" gap="$4">
          <CobuStack gap="$3">
            <CobuHeader
              renderLeft={() => (
                <CobuStack px="$1" py="$1" onPress={handleClose}>
                  <CobuText variant="heading" fos="$4" fow="600" ta="center">
                    {strings.cancel}
                  </CobuText>
                </CobuStack>
              )}
              titleComponent={() => (
                <CobuText variant="heading" fos="$4" fow="600" ta="center">
                  {strings.title}
                </CobuText>
              )}
              renderRight={() => (
                <CobuStack px="$1" py="$1" opacity={isSubmitDisabled ? 0.4 : 1} onPress={handleSubmitPress}>
                  <CobuText variant="heading" fos="$4" fow="600" ta="center">
                    {strings.submit}
                  </CobuText>
                </CobuStack>
              )}
            />

            <CobuStack f={1} gap="$3" px="$4" py="$2">
              <CobuText variant="body" color="$colorSecondary">
                {Strings.format(strings.body, { placeName })}
              </CobuText>
              <Controller
                control={control}
                name="reason"
                render={({ field: { value, onChange, onBlur } }) => (
                  <TextArea
                    placeholder={strings.placeholder}
                    value={value}
                    onChangeText={onChange}
                    onBlur={onBlur}
                    placeholderTextColor="$colorMuted"
                    col="$color"
                    fos="$3"
                    p="$3"
                    mih={'$10'}
                    mah={'60%'}
                    bc="transparent"
                    borderWidth={1}
                    borderColor="$cardBorderColor"
                    multiline
                    numberOfLines={10}
                    maxLength={500}
                    autoFocus
                    verticalAlign="top"
                    autoCapitalize="sentences"
                    scrollEnabled
                    textWrap="wrap"
                    focusStyle={{
                      borderColor: '$black',
                      outlineWidth: 0,
                      outlineColor: 'transparent',
                      shadowColor: 'transparent',
                      borderWidth: 1,
                    }}
                  />
                )}
              />
            </CobuStack>
          </CobuStack>
        </Sheet.Frame>
      </Sheet>
      <CobuAlert
        open={successAlertOpen}
        onOpenChange={setSuccessAlertOpen}
        title={strings.successTitle}
        description={strings.successBody}
        onAccept={() => setSuccessAlertOpen(false)}
      />
      <CobuAlert
        open={errorAlertOpen}
        onOpenChange={setErrorAlertOpen}
        title={strings.errorTitle}
        description={strings.errorBody}
        onAccept={() => setErrorAlertOpen(false)}
      />
    </>
  );
}
